import {isEqual} from 'lodash';
import {settingsService} from "@/modules/settings/services/settingsService";
import {appsService} from "@/modules/appMarket/services/appsService";


const state = {
  isLoaded: false,
  selectedStaff: {},
  hasChanges: false,
  business: {
    timeFormat: null,
    timezone: null,
    firstDayOfWeek: null,
    isLocalBusiness: false,
  },
  contentDisplay: {
    slotColors: null,
    slotIncrement: null,
    slotLayout: null,
  },
  originalSettings: {},
  isSaving: false,
  syncedData: {},
}

const getters = {
  isLoaded: (state) => state.isLoaded,
  selectedStaff: (state) => state.selectedStaff,
  timeFormat: (state) => state.business.timeFormat,
  timezone: (state) => state.business.timezone,
  firstDayOfWeek: (state) => state.business.firstDayOfWeek,
  isLocalBusiness: (state) => state.business.isLocalBusiness,
  originalSettings: (state) => state.originalSettings,
  hasChanges: (state) => state.hasChanges,
  isSaving: (state) => state.isSaving,
  slotColors: (state) => state.contentDisplay.slotColors,
  slotIncrement: (state) => state.contentDisplay.slotIncrement,
  slotLayout: (state) => state.contentDisplay.slotLayout,
  syncedData: (state) => state.syncedData,
}

const actions = {
  async loadInitialState({commit, state, dispatch}, settings) {
    if (state.hasChanges) {
      return;
    }
    commit('setSelectedStaff', settings.selectedStaff);
    commit('setSettingCategory', {category: 'business', settings: settings.business})
    commit('setSettingCategory', {category: 'contentDisplay', settings: settings.contentDisplay})
    commit('setOriginalSettings', {business: {...settings.business}, contentDisplay: {...settings.contentDisplay}});
    commit('setIsLoaded', true);
  },
  async checkSettingsChanged({commit, state}) {
    if (Object.keys(state.originalSettings).some(key => !isEqual(state[key], state.originalSettings[key]))) {
      commit('setHasChanges', true);
    } else {
      commit('setHasChanges', false);
    }
  },
  async resetSettingCategory({commit, state, dispatch}, category) {
    const originalSettings = state.originalSettings[category]
    commit('setSettingCategory', {category, settings: {...originalSettings}})
    dispatch('checkSettingsChanged');
  },
  async updateTimezone({commit, dispatch}, timezone) {
    commit('setTimezone', timezone);
    dispatch('checkSettingsChanged');
  },
  async updateTimeFormat({commit, dispatch}, timeFormat) {
    commit('setTimeFormat', timeFormat);
    dispatch('checkSettingsChanged');
  },
  async updateIsLocalBusiness({commit, dispatch}, isLocalBusiness) {
    commit('setIsLocalBusiness', isLocalBusiness);
    dispatch('checkSettingsChanged');
  },
  async updateFirstDayOfWeek({commit, dispatch}, firstDayOfWeek) {
    commit('setFirstDayOfWeek', firstDayOfWeek);
    dispatch('checkSettingsChanged');
  },
  async updateSelectedStaff({commit}, staff) {
    commit('setSelectedStaff', staff)
  },
  async updateSelectedStaffTimezone({commit}, timezone) {
    commit('setSelectedStaffTimezone', timezone)
  },
  async saveBusinessSettings({state}, {businessId}) {
    const data = state.business;
    const payload = {
      time_zone: data.timezone,
      local_business: data.isLocalBusiness,
      time_format: data.timeFormat,
      first_day: data.firstDayOfWeek.toString(),
    }
    return settingsService.updatePlatformBusinessSetting(businessId, payload)
  },
  async updateOriginalSettings({commit, state, dispatch}, category) {
    commit('setOriginalSettingCategory', {settings: state[category], category});
    dispatch('checkSettingsChanged');
  },
  async updateIsSaving({commit}, isSaving) {
    commit('setIsSaving', isSaving);
  },
  async updateSettingCategory({commit, state}, {category, settings}) {
    commit('setSettingCategory', {category, settings});
    commit('setOriginalSettingCategory', {settings, category});
  },
  async updateSlotColors({commit, dispatch}, slotColors) {
    commit('setSlotColors', slotColors);
    dispatch('checkSettingsChanged');

  },
  async updateSlotIncrement({commit, dispatch}, slotIncrement) {
    commit('setSlotIncrement', slotIncrement);
    dispatch('checkSettingsChanged');
  },
  async updateSlotLayout({commit, dispatch}, slotLayout) {
    commit('setSlotLayout', slotLayout);
    dispatch('checkSettingsChanged');
  },
  async saveStaffContentDisplaySettings({commit, state}, {staffId}) {
    const payload = {
      staff_uid: staffId,
      settings: {
        slot_colors: state.contentDisplay.slotColors,
        slot_intervals: state.contentDisplay.slotIncrement,
        slot_layout: state.contentDisplay.slotLayout,
      }
    }
    return settingsService.saveStaffContentDisplaySettings(staffId, payload);
  },
  async saveSettings({commit, state, dispatch}, {category, businessId}) {
    if (category === 'business') {
      return dispatch('saveBusinessSettings', {businessId});
    }
    if (category === 'contentDisplay') {
      return dispatch('saveStaffContentDisplaySettings', {staffId: state.selectedStaff.uid});
    }
  },
  async clearSettingsStore({commit}) {
    commit('clearSettings');
  },
  async updateSyncData({commit, dispatch}, data) {
    commit('setSyncedData', data);
  },
  async fetchCalendarSync({dispatch}, staffUid) {
    const syncData = await appsService.getCalendarSyncAppPermissions(staffUid);
    dispatch('updateSyncData', syncData);
  },
}

const mutations = {
  setIsLoaded(state, isLoaded) {
    state.isLoaded = isLoaded;
  },
  setSelectedStaff(state, selectedStaff) {
    state.selectedStaff = selectedStaff;
  },
  setTimeFormat(state, timeFormat) {
    state.business.timeFormat = timeFormat;
  },
  setTimezone(state, timezone) {
    state.business.timezone = timezone;
  },
  setSelectedStaffTimezone(state, timezone) {
    state.selectedStaff.time_zone_name = timezone;
    state.selectedStaff.time_zone = timezone;
  },
  setFirstDayOfWeek(state, firstDayOfWeek) {
    state.business.firstDayOfWeek = firstDayOfWeek;
  },
  setIsLocalBusiness(state, isLocalBusiness) {
    state.business.isLocalBusiness = isLocalBusiness;
  },
  setHasChanges(state, hasChanges) {
    state.hasChanges = hasChanges;
  },
  setOriginalSettings(state, originalSettings) {
    state.originalSettings = originalSettings;
  },
  setOriginalSettingCategory(state, {settings, category}) {
    state.originalSettings[category] = {...settings};
  },
  setSettingCategory(state, {category, settings}) {
    state[category] = {...settings};
  },
  setIsSaving(state, isSaving) {
    state.isSaving = isSaving;
  },
  setSlotLayout(state, slotLayout) {
    state.contentDisplay.slotLayout = slotLayout;
  },
  setSlotIncrement(state, slotIncrement) {
    state.contentDisplay.slotIncrement = slotIncrement;
  },
  setSlotColors(state, slotColors) {
    state.contentDisplay.slotColors = slotColors;
  },
  setSyncedData(state, syncedData) {
    state.syncedData = syncedData;
  },
  clearSettings(state) {
    state.isLoaded = false;
    state.selectedStaff = {};
    state.hasChanges = false;
    state.business = {
      timeFormat: null,
      timezone: null,
      firstDayOfWeek: null,
      isLocalBusiness: false,
    };
    state.contentDisplay = {
      slotColors: null,
      slotIncrement: null,
      slotLayout: null,
    };
    state.originalSettings = {};
    state.isSaving = false;
    state.syncedData = {};
  }
}


export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
